import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAdvancedWelcomeTourOptional } from "../../../context/AdvancedWelcomeTourContext";
import "../../../assets/css/advanced-welcome-tour.css";

function getTooltipPosition(targetRect, placement = "bottom") {
  if (!targetRect) {
    return {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      arrow: null,
    };
  }

  const cardWidth = 420;
  const cardHeight = 280;
  const gap = 18;
  const margin = 16;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top = targetRect.top + targetRect.height + gap;
  let left = targetRect.left + targetRect.width / 2 - cardWidth / 2;
  let arrow = "top";

  if (placement === "right") {
    top = targetRect.centerY - cardHeight / 2;
    left = targetRect.left + targetRect.width + gap;
    arrow = "left";
  } else if (placement === "left") {
    top = targetRect.centerY - cardHeight / 2;
    left = targetRect.left - cardWidth - gap;
    arrow = "right";
  } else if (placement === "top") {
    top = targetRect.top - cardHeight - gap;
    left = targetRect.left + targetRect.width / 2 - cardWidth / 2;
    arrow = "bottom";
  }

  left = Math.min(Math.max(left, margin), vw - cardWidth - margin);
  top = Math.min(Math.max(top, margin), vh - cardHeight - margin);

  return { top, left, arrow };
}

export default function AdvancedWelcomeTour() {
  const { t } = useTranslation();
  const tour = useAdvancedWelcomeTourOptional();

  const layout = useMemo(() => {
    if (!tour?.activeStep) return null;
    if (tour.activeStep.centered || !tour.targetRect) {
      return getTooltipPosition(null);
    }
    return getTooltipPosition(tour.targetRect, tour.activeStep.placement);
  }, [tour?.activeStep, tour?.targetRect]);

  if (!tour?.isActive || !tour.activeStep || !layout) {
    return null;
  }

  const { activeStep, stepIndex, totalSteps, targetRect, nextStep, prevStep, skipTour, isTransitioning } =
    tour;

  const isLast = stepIndex >= totalSteps - 1;
  const isCentered = Boolean(activeStep.centered || !targetRect);
  const progressPercent = Math.round(((stepIndex + 1) / totalSteps) * 100);
  const checklist = activeStep.checklistKeys || [];

  return (
    <div className="advanced-welcome-tour" role="presentation">
      <div className="advanced-welcome-tour__backdrop" onClick={skipTour} aria-hidden="true" />

      {targetRect ? (
        <>
          <div
            className="advanced-welcome-tour__highlight"
            style={{
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height,
            }}
          />
          {activeStep.captureLabelKey ? (
            <span
              className="advanced-welcome-tour__capture-label"
              style={{
                top: Math.max(targetRect.top - 34, 8),
                left: targetRect.left,
              }}
            >
              <i className="fas fa-crosshairs" aria-hidden="true" />
              {t(activeStep.captureLabelKey)}
            </span>
          ) : null}
        </>
      ) : null}

      <div
        className={`advanced-welcome-tour__card${isCentered ? " advanced-welcome-tour__card--centered" : ""}${
          layout.arrow ? ` advanced-welcome-tour__card--arrow-${layout.arrow}` : ""
        }`}
        style={
          isCentered
            ? { top: layout.top, left: layout.left, transform: layout.transform }
            : { top: layout.top, left: layout.left }
        }
        role="dialog"
        aria-live="polite"
      >
        <div className="advanced-welcome-tour__card-top">
          <div className="advanced-welcome-tour__visual" aria-hidden="true">
            <span className="advanced-welcome-tour__visual-icon">
              <i className={`fas ${activeStep.icon || "fa-compass"}`} />
            </span>
            <div className="advanced-welcome-tour__visual-mock">
              <span />
              <span />
              <span />
            </div>
          </div>

          <div className="advanced-welcome-tour__card-content">
            <p className="advanced-welcome-tour__eyebrow">
              {t("advTourStepCounter", { current: stepIndex + 1, total: totalSteps })}
            </p>
            <h3>{t(activeStep.titleKey)}</h3>
            <p className="advanced-welcome-tour__body">{t(activeStep.bodyKey)}</p>

            {checklist.length > 0 ? (
              <ul className="advanced-welcome-tour__checklist">
                {checklist.map((key) => (
                  <li key={key}>
                    <i className="fas fa-check-circle" aria-hidden="true" />
                    {t(key)}
                  </li>
                ))}
              </ul>
            ) : null}

            {activeStep.tipKey ? (
              <p className="advanced-welcome-tour__tip">
                <i className="fas fa-lightbulb" aria-hidden="true" />
                {t(activeStep.tipKey)}
              </p>
            ) : null}
          </div>
        </div>

        <div className="advanced-welcome-tour__progress" aria-hidden="true">
          <div className="advanced-welcome-tour__progress-bar" style={{ width: `${progressPercent}%` }} />
        </div>

        <div className="advanced-welcome-tour__actions">
          <button
            type="button"
            className="advanced-welcome-tour__btn advanced-welcome-tour__btn--ghost"
            onClick={skipTour}
          >
            {t("advTourSkip")}
          </button>
          <div className="advanced-welcome-tour__nav">
            <button
              type="button"
              className="advanced-welcome-tour__btn advanced-welcome-tour__btn--ghost"
              onClick={() => void prevStep()}
              disabled={stepIndex === 0 || isTransitioning}
            >
              {t("tourPrevious")}
            </button>
            <button
              type="button"
              className="advanced-welcome-tour__btn advanced-welcome-tour__btn--primary"
              onClick={() => void nextStep()}
              disabled={isTransitioning}
            >
              {isLast ? t("advTourFinishCta") : t("tourNext")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
